import { useEffect, useRef, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import cn from 'clsx'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { toFixedUntilLastNonZero } from '@App/helpers/toFixedUntilLastNonZero'
import { useAppDispatch, useAppSelector } from '@App/hooks'
import {
  getTickerData,
  subscribeToPublicService,
  unsubscribeToPublicService,
} from '@App/redux/slices/sockets/PublicServiceConnectionSlice'
import { Watchlist, WatchlistItem } from '@App/redux/slices/watchlist/slice'
import { Events } from '@App/sockets/types'
import { DeleteIcon } from '@App/static/icons/AccountMenuIcon'
import { CheckIcon } from '@App/static/icons/FooterIcons'
import { AddIcon, CrossIcon, PlusWithBGIcon } from '@App/static/icons/SpotOrderIcons'
import { TickerPrice } from '@App/ui/crypto_exchange/desktop/pages/discover/components/Fallers/components/TickerPrice'
import { Column, Table } from '@App/ui/crypto_exchange/desktop/pages/discover/components/Table'
import { AddToWatchlist } from '@App/ui/crypto_exchange/desktop/pages/spot/components/SpotOrderMarketsTable/components/AddToWatchlist'
import { EmptyListIcon, MinusCircle } from '@App/static/icons/SimpleEarnIcons'
import { setModal } from '@App/redux/slices/modals/ModalSlice'
import { MODAL_TYPES } from '@App/redux/slices/modals/enums'
import { WatchlistItemRow } from '@App/ui/crypto_exchange/desktop/pages/spot/components/SpotOrderMarketsTable/components/SpotOrderMarketsTableSideBar/WatchlistItem'

import { IShowMarketsUpdated } from '@UI/crypto_exchange/desktop/pages/spot/components/SpotOrderMarketsTable'

import { Popover } from '../../../../common/PopOver'
import { Button } from '../../../../controls/Button'
import { TextField } from '../../../../controls/TextField'

import s from './styles.module.scss'
import { useGetConfirmation } from '@App/helpers/confirmation'
import { MarketStatusIcon } from '../../../../common/MarketStatusIcon'

const nameSchema = z.object({
  name: z.string().regex(/[a-zA-Zа-яА-ЯёЁ0-9]/, 'Name must contain at least one letter or number'),
})

type NameFormData = z.infer<typeof nameSchema>

type WatchlistDetailsProps = {
  watchlist: Watchlist | null
  markets: IShowMarketsUpdated[]
  onEditName?: (id: string, name: string) => void
  onDelete?: (id: string) => void
  selectedWatchListItemId: string | null
  setSelectedWatchListItemId: (val: string | null) => void
  onRemoveMarket?: (marketId: number) => void
  onMoveMarket?: (marketId: number, newOrder: number) => void
  onSubfilterSelect?: (subfilterId: string | null) => void
  selectedSubfilterId?: string | null
}

export const WatchlistDetails = ({
  watchlist,
  markets,
  onEditName,
  onDelete,
  onRemoveMarket,
  onMoveMarket,
  selectedWatchListItemId,
  setSelectedWatchListItemId,
  onSubfilterSelect,
  selectedSubfilterId,
}: WatchlistDetailsProps) => {
  const [isEditingName, setIsEditingName] = useState(false)
  const [openSubfilters, setOpenSubfilters] = useState<Set<string>>(new Set())
  const inputRef = useRef<HTMLInputElement>(null)
  const dispatch = useAppDispatch()
  const getConfirmation = useGetConfirmation()
  const onAddMarkets = () => {
    dispatch(setModal({ modalType: MODAL_TYPES.ADD_MARKETS_TO_WATCHLIST, useStack: true }))
  }
  const handleReorder = (
    _reorderedMarkets: IShowMarketsUpdated[],
    { movedId, newIndex }: { movedId: string | number; oldIndex: number; newIndex: number },
  ) => {
    if (!onMoveMarket || !watchlist) return
    onMoveMarket(movedId as number, newIndex)
  }

  const handleDeleteWatchlist = async (id: string) => {
    const confirmed = await getConfirmation({
      closeText: 'Cancel',
      confirmText: 'Delete',
      cancelBtnProps: { variant: 'outlined' },
      confirmBtnProps: { color: 'delete' },
      title: `Are you sure you want to delete ‘’${watchlist?.name}’’?`,
      description: '',
    })
    if (confirmed) {
      onDelete?.(id)
    }
  }
  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<NameFormData>({
    resolver: zodResolver(nameSchema),
    mode: 'onChange',
    defaultValues: {
      name: watchlist?.name ?? '',
    },
  })

  const { ref: registerRef, ...registerRest } = register('name')

  useEffect(() => {
    if (isEditingName) {
      inputRef.current?.focus()
    }
  }, [isEditingName])

  const handleStartEdit = () => {
    reset({ name: watchlist?.name ?? '' })
    setIsEditingName(true)
  }

  const handleCancelEdit = () => {
    reset({ name: watchlist?.name ?? '' })
    setIsEditingName(false)
  }

  const onSubmit = (data: NameFormData) => {
    if (watchlist) {
      onEditName?.(watchlist.key, data.name.trim())
      setIsEditingName(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancelEdit()
    }
  }

  if (!watchlist) {
    return (
      <div className={s.container}>
        <div className={s.empty}>
          <span className={s.emptyText}>Select a watchlist to view details</span>
        </div>
      </div>
    )
  }

  const isCustom = watchlist.isCustom

  const columns: Column<IShowMarketsUpdated>[] = [
    {
      title: 'Market',
      key: 'title',
      render: (_, row) => (
        <MarketColumn market={row} showRemoveButton={watchlist.isCustom} onRemoveMarket={onRemoveMarket} />
      ),
      width: '40%',
    },
    {
      title: 'Sell',
      key: 'source_currency',
      width: '20%',
      headerAlign: 'end',
      render: (_, r) => (
        <PriceColumn source_currency={r.source_currency} target_currency={r.target_currency} field="ask" />
      ),
    },
    {
      title: 'Buy',
      key: 'target_currency',
      width: '20%',
      headerAlign: 'end',
      render: (_, r) => (
        <PriceColumn source_currency={r.source_currency} target_currency={r.target_currency} field="bid" />
      ),
    },
    {
      title: 'Change',
      key: 'percentChange',
      width: '20%',
      headerAlign: 'end',
      render: (v) => (
        <span className={cn(s.flexEnd, v < 0 ? s.negative : v > 0 ? s.positive : '')}>
          {v > 0 ? '+' : ''}
          {toFixedUntilLastNonZero(v, 2)}%
        </span>
      ),
    },
  ]
  const isFavourites = watchlist.key === 'favourites'

  return (
    <div className={s.container}>
      <div className={s.header}>
        <div className={s.titleRow}>
          {isEditingName ? (
            <form className={s.watchlistNameEdit} onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
              <TextField
                {...registerRest}
                ref={(e) => {
                  registerRef(e)
                  inputRef.current = e
                }}
                className={s.editInput}
              />
              <Button type="submit" variant="text" color="check" disabled={!isValid} className={s.editBtn}>
                <CheckIcon className={s.icon} />
              </Button>
              <Button type="button" variant="text" color="delete" onClick={handleCancelEdit} className={s.editBtn}>
                <CrossIcon className={s.icon} />
              </Button>
            </form>
          ) : (
            <>
              <h2 className={s.title}>{watchlist.name}</h2>
              {isCustom && !isFavourites && (
                <Button variant="text" className={s.editNameBtn} onClick={handleStartEdit}>
                  Edit name
                </Button>
              )}
            </>
          )}
        </div>
        {isCustom && !isFavourites && (
          <Button
            variant="text"
            color="delete"
            className={s.deleteBtn}
            onClick={() => handleDeleteWatchlist(watchlist.key)}
          >
            <DeleteIcon className={s.deleteIcon} />
          </Button>
        )}
      </div>
      {isCustom && markets.length > 0 && (
        <div className={s.addMarketsContainer}>
          <Button variant="outlined" onClick={onAddMarkets} className={s.addBtn}>
            <PlusWithBGIcon className={s.plusIcon} />
            Add markets
          </Button>
        </div>
      )}
      <div className={s.content}>
        {watchlist?.items?.length > 0 && (
          <div className={s.watchListItemsContainer}>
            {watchlist.items.map((item) => {
              const hasSubfilters = Array.isArray(item.subfilters) && item.subfilters.length > 0
              const isSubfilterOpen = openSubfilters.has(item.key)
              const isActive = hasSubfilters
                ? item.key === selectedWatchListItemId || (item.subfilters as WatchlistItem[]).some((sf) => sf.value === selectedSubfilterId)
                : item.key === selectedWatchListItemId

              return (
                <WatchlistItemRow
                  key={item.key}
                  item={item}
                  isActive={isActive}
                  onClick={() => setSelectedWatchListItemId(item.key)}
                  hasSubfilters={hasSubfilters}
                  isSubfilterOpen={isSubfilterOpen}
                  onToggleSubfilters={() => {
                    setOpenSubfilters((prev) => {
                      const next = new Set(prev)
                      if (next.has(item.key)) {
                        next.delete(item.key)
                      } else {
                        next.add(item.key)
                      }
                      return next
                    })
                  }}
                  subfilters={hasSubfilters ? (item.subfilters as WatchlistItem[]) : undefined}
                  selectedTab={selectedSubfilterId}
                  onSubfilterClick={(subfilter) => {
                    onSubfilterSelect?.(subfilter.value ?? null)
                  }}
                />
              )
            })}
          </div>
        )}
        {markets.length > 0 ? (
          <Table
            columns={columns}
            data={markets}
            rowKey={(row) => row.id}
            rowClassName={() => s.tableRow}
            bodyClassName={s.tableBody}
            sortable={
              isCustom && onMoveMarket
                ? {
                    enabled: true,
                    onReorder: handleReorder,
                  }
                : undefined
            }
          />
        ) : (
          <div className={s.emptyState}>
            <EmptyListIcon className={s.emptyListIcon} />
            <span className={s.emptyText}>This list is currently empty</span>
            {isCustom && (
              <Button className={s.addBtn} onClick={onAddMarkets}>
                <PlusWithBGIcon className={s.plusIcon} />
                Add markets
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const MarketColumn = ({
  market,
  showRemoveButton,
  onRemoveMarket,
}: {
  market: IShowMarketsUpdated
  showRemoveButton?: boolean
  onRemoveMarket?: (id: number) => void
}) => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(
      subscribeToPublicService({
        event: Events.GetRate,
        params: {
          component: 'CurrencyRateBlock',
          source_currency: market.source_currency,
          target_currency: market.target_currency,
        },
      }),
    )

    return () => {
      dispatch(
        unsubscribeToPublicService({
          event: Events.GetRate,
          params: {
            component: 'CurrencyRateBlock',
            source_currency: market.source_currency,
            target_currency: market.target_currency,
          },
        }),
      )
    }
  }, [market.source_currency, market.target_currency])

  return (
    <span className={s.marketTitle}>
      <span className={s.addContainer}>
        <MarketStatusIcon market={market} className={s.marketStatus} />
        {showRemoveButton && <MinusCircle onClick={() => onRemoveMarket?.(market.id)} />}
        {!showRemoveButton && (
          <Popover
            trigger={<AddIcon width={16} />}
            closeOnSelect={false}
            closeOnScroll
            position="bottom"
            align="start"
            renderInPortal={false}
          >
            {(close) => <AddToWatchlist market={market} onClose={close} />}
          </Popover>
        )}
      </span>
      {market.title}
    </span>
  )
}

const PriceColumn = ({
  source_currency,
  target_currency,
  field,
}: {
  source_currency: string
  target_currency: string
  field: 'ask' | 'bid'
}) => {
  const tickerData = useAppSelector((state) => getTickerData(state, source_currency, target_currency))
  const price = tickerData[field]
  return (
    <span className={s.flexEnd}>
      <TickerPrice price={price} classNames={s.tickerValue}>
        {price ?? <span>&mdash;</span>}
      </TickerPrice>
    </span>
  )
}
